import { init_uimessage_stream_parser, init_voice_client, voice_client_exports, parseUIMessageStream } from './chunk-7H37YIE3.mjs';
import { __toCommonJS } from './chunk-NFEGQTCC.mjs';

// src/auth.ts
function buildAuthHeaders(apiKey) {
  const headers = { "Content-Type": "application/json" };
  let queryParams = "";
  if (apiKey.startsWith("pub_")) {
    headers["x-public-key"] = apiKey;
    queryParams = `publicKey=${encodeURIComponent(apiKey)}`;
  } else {
    headers["x-api-key"] = apiKey;
  }
  return { headers, queryParams };
}
function isPublicKey(apiKey) {
  return apiKey.startsWith("pub_");
}

// src/chat/client-context.ts
var UTM_KEYS = [
  ["utm_source", "source"],
  ["utm_medium", "medium"],
  ["utm_campaign", "campaign"],
  ["utm_term", "term"],
  ["utm_content", "content"]
];
function parseUtm(search) {
  let params;
  try {
    params = new URLSearchParams(search || "");
  } catch {
    return void 0;
  }
  const utm = {};
  let found = false;
  for (const [param, field] of UTM_KEYS) {
    const value = params.get(param);
    if (value) {
      utm[field] = value;
      found = true;
    }
  }
  return found ? utm : void 0;
}
function collectClientContext() {
  if (typeof window === "undefined") return void 0;
  const ctx = {};
  try {
    const nav = typeof navigator !== "undefined" ? navigator : void 0;
    if (nav?.userAgent) ctx.userAgent = nav.userAgent;
    if (nav?.language) ctx.language = nav.language;
  } catch {
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) ctx.timezone = tz;
  } catch {
  }
  try {
    const scr = typeof window.screen !== "undefined" ? window.screen : void 0;
    if (scr && typeof scr.width === "number" && typeof scr.height === "number") {
      ctx.screen = `${scr.width}x${scr.height}`;
    }
  } catch {
  }
  try {
    const ref = typeof document !== "undefined" ? document.referrer : "";
    if (ref) ctx.referrer = ref;
  } catch {
  }
  try {
    const href = typeof location !== "undefined" ? location.href : "";
    if (href) ctx.pageUrl = href;
    const utm = parseUtm(typeof location !== "undefined" ? location.search : "");
    if (utm) ctx.utm = utm;
  } catch {
  }
  return Object.keys(ctx).length > 0 ? ctx : void 0;
}
function mergeClientContext(base, override) {
  if (!base && !override) return void 0;
  const merged = { ...base || {}, ...override || {} };
  const baseUtm = base?.utm;
  const overrideUtm = override?.utm;
  if (baseUtm || overrideUtm) {
    const utm = { ...baseUtm || {}, ...overrideUtm || {} };
    if (Object.keys(utm).length > 0) merged.utm = utm;
    else delete merged.utm;
  }
  return Object.keys(merged).length > 0 ? merged : void 0;
}

// src/chat/http.ts
function unwrap(data) {
  if (data?.data?.data) return data.data.data;
  if (data?.data) return data.data;
  return data;
}
async function readErrorDetail(res) {
  let text = "";
  try {
    text = await res.text();
  } catch {
    return "";
  }
  if (!text) return "";
  try {
    const json = JSON.parse(text);
    const msg = json?.error?.message ?? json?.message ?? (typeof json?.error === "string" ? json.error : void 0);
    if (typeof msg === "string" && msg) return msg;
  } catch {
  }
  return text.slice(0, 300);
}

// src/chat/convo-client.ts
init_uimessage_stream_parser();
var CONVO_BASE_PATH = "/api/v1/convo";
function createConvoChatClient(config) {
  const {
    apiKey,
    baseUrl,
    user: configUser,
    userHash: configUserHash,
    anonymousId: configAnonymousId
  } = config;
  const base = baseUrl.replace(/\/$/, "");
  const auth = buildAuthHeaders(apiKey);
  const grants = /* @__PURE__ */ new Map();
  let bootedAgentId;
  const rememberGrant = (conversationId, token) => {
    if (conversationId && token) grants.set(conversationId, token);
  };
  const query = (params) => {
    const pairs = Object.entries(params).filter(([, v]) => typeof v === "string" && v.length > 0).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
    if (auth.queryParams) pairs.push(auth.queryParams);
    return pairs.length > 0 ? `?${pairs.join("&")}` : "";
  };
  const grantHeaders = (conversationId, explicitGrant) => {
    const grant = explicitGrant ?? grants.get(conversationId);
    return grant ? { ...auth.headers, "X-Conversation-Grant": grant } : { ...auth.headers };
  };
  const bootBody = (input) => {
    const body = {};
    const user = input?.user ?? configUser;
    const userHash = input?.userHash ?? configUserHash;
    const anonymousId = input?.anonymousId ?? configAnonymousId;
    if (user) body.user = user;
    if (userHash) body.userHash = userHash;
    if (anonymousId) body.anonymousId = anonymousId;
    if (input?.customerId) body.customerId = input.customerId;
    if (input?.variables) body.variables = input.variables;
    if (input?.systemPrompt) body.systemPrompt = input.systemPrompt;
    if (input?.metadata) body.metadata = input.metadata;
    const clientContext = mergeClientContext(collectClientContext(), input?.clientContext);
    if (clientContext) body.clientContext = clientContext;
    return body;
  };
  const toSession = (raw, agentId) => {
    const conversationId = raw?.conversationId ?? raw?.interactionId ?? raw?.sessionId;
    rememberGrant(conversationId, raw?.sessionToken);
    return {
      interactionId: conversationId,
      sessionId: conversationId,
      agentId: raw?.agentId ?? agentId,
      agentName: raw?.agentName,
      model: raw?.model,
      createdAt: raw?.createdAt ?? (/* @__PURE__ */ new Date()).toISOString(),
      sessionToken: raw?.sessionToken,
      reengagement: raw?.reengagement,
      ...raw?.reattached === true && { reattached: true },
      ...raw?.continued === true && { continued: true },
      ...Array.isArray(raw?.messages) && raw.messages.length > 0 && { messages: raw.messages },
      ...raw?.threadId && { threadId: raw.threadId }
    };
  };
  async function boot(agentId, input) {
    const url = `${base}${CONVO_BASE_PATH}/boot${query({})}`;
    const res = await fetch(url, {
      method: "POST",
      headers: auth.headers,
      body: JSON.stringify({ ...bootBody(input), agentId })
    });
    if (!res.ok) {
      const detail = await readErrorDetail(res);
      throw new Error(`Boot failed (${res.status})${detail ? `: ${detail}` : ""}`);
    }
    bootedAgentId = agentId;
    return toSession(unwrap(await res.json()), agentId);
  }
  async function postTurn(conversationId, message, mode) {
    if (!bootedAgentId) {
      throw new Error(
        "No agent on this client \u2014 call createSession(agentId, \u2026) before posting a turn."
      );
    }
    const url = `${base}${CONVO_BASE_PATH}/${encodeURIComponent(conversationId)}/message${query({
      agentId: bootedAgentId
    })}`;
    const res = await fetch(url, {
      method: "POST",
      headers: grantHeaders(conversationId),
      body: JSON.stringify({ message, mode })
    });
    if (!res.ok) {
      const detail = await readErrorDetail(res);
      const hint = res.status === 410 ? " \u2014 the runtime grant expired; re-boot this conversation" : "";
      throw new Error(`Turn failed (${res.status})${detail ? `: ${detail}` : ""}${hint}`);
    }
    return res;
  }
  return {
    async createSession(agentId, input) {
      return boot(agentId, input);
    },
    async streamMessage(sessionId, message, onChunk, onPart) {
      const start = Date.now();
      const res = await postTurn(sessionId, message, "stream");
      const contentType = typeof res.headers?.get === "function" ? res.headers.get("content-type") ?? "" : "";
      if (contentType.includes("application/json") || !res.body) {
        const raw = unwrap(await res.json());
        return {
          sessionId,
          message: raw?.message ?? "",
          toolCalls: raw?.toolCalls,
          latencyMs: raw?.latencyMs ?? Date.now() - start,
          suppressed: raw?.suppressed === true,
          error: null,
          ...raw?.escalation ? { escalation: raw.escalation } : {},
          // Takeover visibility (#144 Epic D): the suppressed JSON names WHO owns the
          // thread — dropping it here is how the widget rendered an anonymous handoff.
          ...raw?.handoff ? { handoff: raw.handoff } : {}
        };
      }
      const parsed = await parseUIMessageStream(res.body, onChunk, onPart);
      return {
        sessionId,
        message: parsed.text,
        parts: parsed.parts.length > 0 ? parsed.parts : void 0,
        toolCalls: parsed.toolCalls.length > 0 ? parsed.toolCalls : void 0,
        latencyMs: Date.now() - start,
        suppressed: parsed.suppressed,
        error: parsed.error,
        ...parsed.suppression ? { suppression: parsed.suppression } : {},
        ...parsed.handoff ? { handoff: parsed.handoff } : {},
        ...parsed.escalation ? { escalation: parsed.escalation } : {},
        ...parsed.signalError ? { signalError: parsed.signalError } : {}
      };
    },
    async sendMessage(sessionId, message) {
      const res = await postTurn(sessionId, message, "sync");
      const raw = unwrap(await res.json());
      return {
        sessionId,
        message: raw?.message ?? "",
        toolCalls: raw?.toolCalls,
        latencyMs: raw?.latencyMs,
        suppressed: raw?.suppressed === true,
        error: null,
        ...raw?.escalation ? { escalation: raw.escalation } : {}
      };
    },
    /**
     * End the conversation and close its thread.
     *
     * Resolves even when the close fails: the sole callers are teardown paths — `disconnect()`
     * and a React unmount effect, one of which does not catch — so rejecting here would surface
     * as an unhandled rejection during cleanup. The failure is logged rather than swallowed,
     * because a close that never lands means no analysis and an open thread, with nothing
     * otherwise visible to anyone.
     */
    async endSession(sessionId) {
      const url = `${base}${CONVO_BASE_PATH}/${encodeURIComponent(sessionId)}/close${query({})}`;
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: grantHeaders(sessionId),
          body: "{}"
        });
        if (!res.ok) {
          console.warn(`[chanl] close failed (${res.status}) \u2014 conversation ${sessionId} stays open`);
        }
      } catch (err) {
        console.warn("[chanl] close failed", err);
      }
    },
    /**
     * Resume a conversation on a token persisted across a page load.
     *
     * The token IS the runtime grant, so it is sent as the Bearer credential rather than as a
     * body field or a query param — this route has no other way to prove the conversation is
     * the caller's. The response is a `BootResponse`, the same shape boot returns, carrying a
     * refreshed grant that replaces the stored one.
     */
    async resumeSession(interactionId, sessionToken) {
      const url = `${base}${CONVO_BASE_PATH}/${encodeURIComponent(interactionId)}/resume${query({})}`;
      const res = await fetch(url, {
        method: "POST",
        headers: grantHeaders(interactionId, sessionToken),
        body: "{}"
      });
      if (!res.ok) {
        const detail = await readErrorDetail(res);
        throw new Error(`Resume failed (${res.status})${detail ? `: ${detail}` : ""}`);
      }
      const raw = unwrap(await res.json());
      const conversationId = raw?.conversationId ?? raw?.interactionId ?? raw?.sessionId ?? interactionId;
      rememberGrant(conversationId, raw?.sessionToken);
      if (raw?.agentId) bootedAgentId = raw.agentId;
      return {
        interactionId: conversationId,
        sessionId: conversationId,
        agentId: raw?.agentId,
        agentName: raw?.agentName,
        status: raw?.status,
        createdAt: raw?.createdAt || (/* @__PURE__ */ new Date()).toISOString(),
        messages: raw?.messages || [],
        sessionToken: raw?.sessionToken
      };
    },
    /**
     * Identify a visitor mid-conversation — Intercom-style.
     *
     * The conversation opened anonymously, the visitor exchanged a few messages, and the host
     * app has now authenticated them. The platform verifies `userHash`, runs Lead → User
     * conversion against its Customer record, and rebinds the conversation in place; the
     * agent's NEXT reply picks up the new identity, history preserved.
     *
     * The re-issued `sessionToken` is bound to the now-identified customer and MUST replace the
     * stored one — keeping the anonymous grant is what makes the next call 403.
     */
    async identify(sessionId, input) {
      const url = `${base}${CONVO_BASE_PATH}/${encodeURIComponent(sessionId)}/identify${query({})}`;
      const body = { user: input.user };
      if (input.userHash) body.userHash = input.userHash;
      const res = await fetch(url, {
        method: "POST",
        headers: grantHeaders(sessionId),
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const detail = await readErrorDetail(res);
        throw new Error(`Identify failed (${res.status})${detail ? `: ${detail}` : ""}`);
      }
      const raw = unwrap(await res.json());
      rememberGrant(sessionId, raw?.sessionToken);
      return {
        sessionId: raw?.sessionId ?? sessionId,
        customerId: raw?.customerId,
        created: !!raw?.created,
        sessionToken: raw?.sessionToken,
        // Distinguishes a real identification from one that bound but left the caller holding
        // its anonymous credential — an unproven identify still answers 200.
        ...typeof raw?.identityProven === "boolean" && { identityProven: raw.identityProven }
      };
    },
    /** Delete the conversation and its thread. */
    async deleteSession(sessionId) {
      const url = `${base}${CONVO_BASE_PATH}/${encodeURIComponent(sessionId)}${query({})}`;
      const res = await fetch(url, { method: "DELETE", headers: grantHeaders(sessionId) });
      if (!res.ok) {
        const detail = await readErrorDetail(res);
        throw new Error(`Delete failed (${res.status})${detail ? `: ${detail}` : ""}`);
      }
      const raw = unwrap(await res.json());
      grants.delete(sessionId);
      return { deleted: raw?.deleted === true };
    },
    /**
     * Page a conversation's turns. Used by the chat-host iframe to restore a thread on reload.
     *
     * Returns `null` when the conversation is unknown, expired or not the caller's, so the
     * caller can fall back to booting a fresh one.
     */
    async getMessages(sessionId) {
      const url = `${base}${CONVO_BASE_PATH}/${encodeURIComponent(sessionId)}/messages${query({})}`;
      const res = await fetch(url, { method: "GET", headers: grantHeaders(sessionId) });
      if (!res.ok) return null;
      const raw = unwrap(await res.json());
      if (!raw || !Array.isArray(raw.messages)) return null;
      return { sessionId, messages: raw.messages, toolCalls: [] };
    },
    /**
     * `POST /convo/:id/nudge` — the server-authoritative half of re-engagement. The widget's
     * idle timer only PROPOSES; the route re-validates against message timestamps and the
     * per-session budget, so a misbehaving client cannot spam nudges or end sessions early.
     * Errors resolve to `skip` (keep-waiting), never throw: the timer's catch used to swallow
     * a thrown gap silently, and a nudge must never surface an error to the visitor.
     */
    async nudge(sessionId) {
      const url = `${base}${CONVO_BASE_PATH}/${encodeURIComponent(sessionId)}/nudge${query({})}`;
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: grantHeaders(sessionId),
          body: "{}"
        });
        if (!res.ok) {
          return { directive: "skip", reason: `http_${res.status}` };
        }
        const raw = unwrap(await res.json());
        if (raw && (raw.directive === "nudged" || raw.directive === "skip" || raw.directive === "end")) {
          return raw;
        }
        return { directive: "skip", reason: "malformed_response" };
      } catch {
        return { directive: "skip", reason: "network_error" };
      }
    }
  };
}

// src/chat/chat-client.ts
function createChatClient(config) {
  return createConvoChatClient(config);
}

// src/chat/widget-config.ts
async function getWidgetConfig(agentId, apiKey, baseUrl) {
  try {
    const url = `${baseUrl.replace(/\/$/, "")}/api/v1/agents/widget/by-agent?agentId=${encodeURIComponent(agentId)}&publicKey=${encodeURIComponent(apiKey)}`;
    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        "x-public-key": apiKey
      }
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.data || json;
  } catch {
    return null;
  }
}

// src/defaults.ts
var PROD_BASE_URL = "https://platform.channel.tel";
var DEV_BASE_URL = "http://localhost:3100";
function resolveBaseUrl(explicit) {
  if (explicit) return explicit;
  if (typeof window === "undefined") return PROD_BASE_URL;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) {
    return DEV_BASE_URL;
  }
  return PROD_BASE_URL;
}

// src/client.ts
function createChanlClient(config) {
  const { agentId, apiKey } = config;
  const baseUrl = resolveBaseUrl(config.baseUrl);
  let voiceClient = null;
  const getVoice = () => {
    if (!voiceClient) {
      const { VoiceClient: VC } = (init_voice_client(), __toCommonJS(voice_client_exports));
      voiceClient = new VC(apiKey, {
        baseUrl,
        anonymousId: config.anonymousId,
        user: config.user,
        userHash: config.userHash,
        transport: config.transport
      });
    }
    return voiceClient;
  };
  return {
    /** Text chat client (sessions, streaming, history) */
    chat: createChatClient({ apiKey, baseUrl }),
    /** Voice client — lazy-loaded on first access (browser only) */
    get voice() {
      return getVoice();
    },
    /** Fetch agent widget config (name, avatar, style, greeting) */
    getWidgetConfig() {
      return getWidgetConfig(agentId, apiKey, baseUrl);
    },
    /** Resolved configuration for inspection */
    config: { agentId, apiKey, baseUrl }
  };
}

// src/events.ts
var CustomEventEmitter = class {
  constructor() {
    this.listeners = {};
  }
  on(event, listener) {
    var _a;
    ((_a = this.listeners)[event] || (_a[event] = [])).push(listener);
    return this;
  }
  off(event, listener) {
    const arr = this.listeners[event];
    if (arr) this.listeners[event] = arr.filter((l) => l !== listener);
    return this;
  }
  emit(event, ...args) {
    (this.listeners[event] || []).forEach((l) => l(...args));
    return this;
  }
};

// src/runtime/event-bus.ts
var RuntimeEventBus = class extends CustomEventEmitter {
  dispatch(event) {
    this.emit("message", event);
    switch (event.type) {
      case "text-delta":
        this.emit("text-delta", event.delta);
        break;
      case "transcript":
        this.emit("transcript", { role: event.role, text: event.text, final: event.final });
        break;
      case "tool-call":
        this.emit("tool-call", event);
        break;
      case "tool-result":
        this.emit("tool-result", event);
        break;
      case "data":
        this.emit("data", event);
        break;
      case "status":
        this.emit("status", event.state);
        break;
      case "session":
        this.emit("session", event);
        break;
      case "handoff":
        this.emit("handoff", event);
        break;
      case "escalation":
        this.emit("escalation", event);
        break;
      case "audio-level":
        this.emit("audio-level", event.level);
        break;
      case "error":
        this.emit("error", event.error);
        break;
    }
  }
};

// src/runtime/widget-decoder.ts
var counter = 0;
var nextId = () => `w${Date.now().toString(36)}-${counter++}`;
function decodeToolResultWidget(_name, result) {
  if (result && typeof result === "object" && "__chanlWidget" in result) {
    const r = result;
    return { type: "data", id: nextId(), widget: String(r.__chanlWidget), data: r.data ?? r };
  }
  return null;
}
var FENCE = /```chanl:widget\s*([\s\S]*?)```/g;
function extractInlineWidgets(text) {
  const widgets = [];
  const cleanedText = text.replace(FENCE, (_match, body) => {
    try {
      const obj = JSON.parse(body.trim());
      if (obj && typeof obj === "object" && "$widget" in obj) {
        widgets.push({
          type: "data",
          id: nextId(),
          widget: String(obj.$widget),
          schema: typeof obj.$schema === "string" ? obj.$schema : void 0,
          data: "data" in obj ? obj.data : obj
        });
      }
    } catch {
    }
    return "";
  });
  return { widgets, cleanedText: cleanedText.trim() };
}

// src/runtime/chat-mapper.ts
function createChatEventMapper(bus) {
  let textBuffer = "";
  return {
    onChunk(text) {
      textBuffer += text;
      bus.dispatch({ type: "text-delta", role: "agent", delta: text });
    },
    onPart(part) {
      if (part.type !== "tool-invocation" || !part.toolInvocation) return;
      const ti = part.toolInvocation;
      if (ti.state === "call") {
        bus.dispatch({ type: "tool-call", id: ti.toolCallId, name: ti.toolName, args: ti.args, source: "server" });
      } else {
        bus.dispatch({ type: "tool-result", id: ti.toolCallId, name: ti.toolName, result: ti.result });
        const w = decodeToolResultWidget(ti.toolName, ti.result);
        if (w) bus.dispatch(w);
      }
    },
    finalize() {
      const { widgets } = extractInlineWidgets(textBuffer);
      for (const w of widgets) bus.dispatch(w);
      textBuffer = "";
    }
  };
}

// src/runtime/voice-adapter.ts
function attachVoiceAdapter(voice, bus) {
  const subs = [];
  const on = (event, cb) => {
    voice.on(event, cb);
    subs.push([event, cb]);
  };
  on("ready", () => bus.dispatch({ type: "status", state: "ready" }));
  on("volumeLevel", (v) => bus.dispatch({ type: "audio-level", level: v }));
  on("speech-start", () => {
    bus.emit("speech-start");
    bus.dispatch({ type: "status", state: "speaking" });
  });
  on("speech-end", () => {
    bus.emit("speech-end");
    bus.dispatch({ type: "status", state: "listening" });
  });
  on("transcript", (t) => {
    const text = t?.text ?? "";
    bus.dispatch({ type: "transcript", role: t?.role === "user" ? "user" : "agent", text, final: t?.final ?? true });
    const { widgets } = extractInlineWidgets(text);
    for (const w of widgets) bus.dispatch(w);
  });
  on("closed", () => bus.dispatch({ type: "session", phase: "ended", sessionId: "" }));
  on("error", (e) => bus.dispatch({ type: "error", error: e instanceof Error ? e : new Error(String(e)) }));
  return () => {
    if (voice.off) for (const [e, cb] of subs) voice.off(e, cb);
  };
}

// src/runtime/chanl-runtime.ts
function dispatchTurnSignals(bus, res) {
  if (res.handoff) {
    bus.dispatch({
      type: "handoff",
      origin: res.handoff.origin,
      ...res.handoff.reason ? { reason: res.handoff.reason } : {},
      ...res.handoff.threadId ? { threadId: res.handoff.threadId } : {},
      ...res.handoff.assigneeId ? { assigneeId: res.handoff.assigneeId } : {}
    });
  } else if (res.suppressed) {
    bus.dispatch({
      type: "handoff",
      ...res.suppression?.reason ? { reason: res.suppression.reason } : {}
    });
  }
  if (res.escalation) {
    bus.dispatch({
      type: "escalation",
      escalated: res.escalation.escalated,
      ...res.escalation.reason ? { reason: res.escalation.reason } : {},
      ...res.escalation.transitionMessage ? { transitionMessage: res.escalation.transitionMessage } : {}
    });
  }
}
function serializeInject(p) {
  switch (p.type) {
    case "add-message":
      return p.text;
    case "context":
      return `\`\`\`chanl:context
${JSON.stringify(p.data)}
\`\`\``;
    case "trigger":
      return `\`\`\`chanl:trigger
${JSON.stringify({ event: p.event, data: p.data ?? {} })}
\`\`\``;
    case "data":
      return `\`\`\`chanl:response
${JSON.stringify({ widget: p.widget, value: p.value })}
\`\`\``;
  }
}
function createChanlRuntime(config) {
  const { agentId, apiKey } = config;
  const baseUrl = resolveBaseUrl(config.baseUrl);
  const bus = new RuntimeEventBus();
  const chat = createChatClient({ apiKey, baseUrl, wire: config.wire ?? "convo" });
  let sessionId = null;
  let state = "idle";
  let mode = config.mode ?? "text";
  let voice = null;
  let detachVoice = null;
  const setState = (s) => {
    state = s;
    bus.dispatch({ type: "status", state: s });
  };
  async function ensureSession() {
    if (sessionId) return sessionId;
    setState("connecting");
    const s = await chat.createSession(agentId, { anonymousId: config.anonymousId });
    sessionId = s.sessionId;
    bus.dispatch({ type: "session", phase: "created", sessionId: s.sessionId });
    setState("ready");
    return sessionId;
  }
  async function sendText(text) {
    const sid = await ensureSession();
    const mapper = createChatEventMapper(bus);
    setState("thinking");
    try {
      const res = await chat.streamMessage(sid, text, mapper.onChunk, mapper.onPart);
      mapper.finalize();
      dispatchTurnSignals(bus, res);
      if (res.error) bus.dispatch({ type: "error", error: new Error(res.error) });
    } catch (err) {
      bus.dispatch({ type: "error", error: err instanceof Error ? err : new Error(String(err)) });
    } finally {
      setState("ready");
    }
  }
  function getVoice() {
    if (!voice) {
      const { VoiceClient } = (init_voice_client(), __toCommonJS(voice_client_exports));
      voice = new VoiceClient(apiKey, { baseUrl, anonymousId: config.anonymousId });
      detachVoice = attachVoiceAdapter(voice, bus);
    }
    return voice;
  }
  return {
    on: (e, cb) => bus.on(e, cb),
    off: (e, cb) => bus.off(e, cb),
    sendText,
    send: (p) => sendText(serializeInject(p)),
    async startVoice() {
      mode = "voice";
      getVoice().start(agentId);
    },
    stopVoice() {
      voice?.stop();
    },
    disconnect() {
      detachVoice?.();
      voice?.stop();
      if (sessionId) chat.endSession(sessionId).catch(() => {
      });
    },
    get state() {
      return state;
    },
    get mode() {
      return mode;
    }
  };
}

export { RuntimeEventBus, buildAuthHeaders, collectClientContext, createChanlClient, createChanlRuntime, createChatClient, decodeToolResultWidget, extractInlineWidgets, getWidgetConfig, isPublicKey, mergeClientContext, resolveBaseUrl };
//# sourceMappingURL=chunk-G32KXWAY.mjs.map
//# sourceMappingURL=chunk-G32KXWAY.mjs.map